import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, timer, BehaviorSubject } from 'rxjs';
import { takeUntil, switchMap, catchError } from 'rxjs/operators';
import { SnackBarService } from '../../../share/snack-bar.service';
import { environment } from 'src/app/shared/environment';

export interface ProductStatus {
  product_id: number;
  ai_status?: string;
  face_recognition_status?: string;
  timestamp: string;
  upload_session_id: number;
}

export interface StatusResponse {
  upload_session_id: string;
  product_statuses: ProductStatus[];
}

export interface StatusUpdate {
  productId: number;
  aiStatus?: string;
  isProcessing: boolean;
}

export enum ProcessingStatus {
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  DONE = "DONE",
  FAILED = "FAILED"
}

export interface StatusResult {
  aiStatus: string;
  isAiProcessing: boolean;
}


@Injectable({
  providedIn: 'root'
})
export class AIStatusTrackingService {

  constructor(private http: HttpClient, private snackBarService: SnackBarService) {}
  private stopPolling$ = new Subject<void>();
  private activeSessionId?: number;
  private lastStatusUpdateTimestamp: {[productId: number]: string} = {};

  // Novi Subject za obavještavanje o promjenama statusa
  private statusUpdates$ = new Subject<StatusUpdate[]>();
  public statusUpdates: Observable<StatusUpdate[]> = this.statusUpdates$.asObservable();

  public static parseStatus(status: {
    ai_status?: string,
    face_recognition_status?: string
  }): StatusResult {
    const aiStatus = status.ai_status as ProcessingStatus;
    const faceStatus = status.face_recognition_status as ProcessingStatus;

    // Ako nijedan status nije postavljen
    if (!aiStatus && !faceStatus) {
      return {
        aiStatus: "Ready",
        isAiProcessing: false
      };
    }

    // Agregirani status prioritizira stanja prema važnosti:
    // 1. IN_PROGRESS (bilo koji proces)
    // 2. PENDING (bilo koji proces)
    // 3. FAILED (bilo koji proces)
    // 4. DONE (oba procesa moraju biti DONE ili jedan DONE, a drugi nepostojećeg)

    let aggregatedStatus: string;

    // Ako je bilo koji proces IN_PROGRESS, to ima najviši prioritet
    if (aiStatus === ProcessingStatus.IN_PROGRESS || faceStatus === ProcessingStatus.IN_PROGRESS) {

      if (aiStatus === ProcessingStatus.IN_PROGRESS && faceStatus === ProcessingStatus.IN_PROGRESS) {
        aggregatedStatus = "AI and face recognition processing...";
      } else if (aiStatus === ProcessingStatus.IN_PROGRESS) {
        aggregatedStatus = "AI processing...";
      } else {
        aggregatedStatus = "Face recognition processing...";
      }
    }
    // Ako je bilo koji proces PENDING, to je sljedeći prioritet
    else if (aiStatus === ProcessingStatus.PENDING || faceStatus === ProcessingStatus.PENDING) {

      if (aiStatus === ProcessingStatus.PENDING && faceStatus === ProcessingStatus.PENDING) {
        aggregatedStatus = "AI and face recognition queued...";
      } else if (aiStatus === ProcessingStatus.PENDING) {
        aggregatedStatus = "AI queued...";
      } else {
        aggregatedStatus = "Face recognition queued...";
      }
    }
    // Ako je bilo koji proces FAILED, to je sljedeći prioritet
    else if (aiStatus === ProcessingStatus.FAILED || faceStatus === ProcessingStatus.FAILED) {
      if (aiStatus === ProcessingStatus.FAILED && faceStatus === ProcessingStatus.FAILED) {
        aggregatedStatus = "All processing failed";
      } else if (aiStatus === ProcessingStatus.FAILED) {
        // Provjeri i drugi status
        if (faceStatus === ProcessingStatus.DONE) {
          aggregatedStatus = "AI failed, face recognition complete";
        } else {
          aggregatedStatus = "AI processing failed";
        }
      } else {
        // Provjeri i drugi status
        if (aiStatus === ProcessingStatus.DONE) {
          aggregatedStatus = "Face recognition failed, AI complete";
        } else {
          aggregatedStatus = "Face recognition failed";
        }
      }
    }
    // Ako su oba procesa DONE ili jedan DONE, a drugi ne postoji
    else if ((aiStatus === ProcessingStatus.DONE || !aiStatus) &&
      (faceStatus === ProcessingStatus.DONE || !faceStatus)) {

      if (aiStatus === ProcessingStatus.DONE && faceStatus === ProcessingStatus.DONE) {
        aggregatedStatus = "All processing complete";
      } else if (aiStatus === ProcessingStatus.DONE) {
        aggregatedStatus = "AI processing complete";
      } else {
        aggregatedStatus = "Face recognition complete";
      }
    }
    // Catch-all za sve ostale kombinacije
    else {
      aggregatedStatus = "Processing...";
    }

    return {
      aiStatus: aggregatedStatus,
      isAiProcessing: true
    };
  }

  // Pomoćna metoda koja određuje ukupni status obrade
  public static getProcessingState(aiStatus?: string, faceStatus?: string): boolean {
    // Procesi su u tijeku ako je bilo koji IN_PROGRESS ili PENDING
    return (
      aiStatus === ProcessingStatus.PENDING ||
      aiStatus === ProcessingStatus.IN_PROGRESS ||
      faceStatus === ProcessingStatus.PENDING ||
      faceStatus === ProcessingStatus.IN_PROGRESS
    );
  }

  startTracking(uploadSessionId: number): void {
    // Stop any existing tracking
    this.stopTracking();

    this.activeSessionId = uploadSessionId;

    // Poll every 3 seconds for status updates
    timer(0, 3000).pipe(
      takeUntil(this.stopPolling$),
      switchMap(() => this.getSessionStatus(uploadSessionId)),
      catchError(err => {
        console.error('Error getting status updates:', err);
        return [];
      })
    ).subscribe(response => {
      if (response && response.product_statuses) {
        this.processStatusUpdates(response.product_statuses);
      }
    });
  }

  stopTracking(): void {
    this.stopPolling$.next();
    this.activeSessionId = undefined;
  }

  private getSessionStatus(sessionId: number): Observable<StatusResponse> {
    return this.http.get<StatusResponse>(environment.apiUrl + `/cms/ai-status/upload-session/${sessionId}/status`);
  }

  private processStatusUpdates(statuses: ProductStatus[]): void {
    const updates: StatusUpdate[] = [];

    statuses.forEach(status => {
      // Only process updates that are new
      if (this.isNewStatus(status)) {
        const {aiStatus, isAiProcessing } = AIStatusTrackingService.parseStatus({
          ai_status: status.ai_status,
          face_recognition_status: status.face_recognition_status
        });

        updates.push({
          productId: status.product_id,
          aiStatus,
          isProcessing: isAiProcessing
        });

        // Update the last seen timestamp for this product
        this.lastStatusUpdateTimestamp[status.product_id] = status.timestamp;
      }
    });

    // Emit updates only if there are any
    if (updates.length > 0) {
      this.statusUpdates$.next(updates);
    }
  }

  private isNewStatus(status: ProductStatus): boolean {
    const lastTimestamp = this.lastStatusUpdateTimestamp[status.product_id];
    if (!lastTimestamp) { return true; }

    // Check if this status is newer than what we've seen before
    return new Date(status.timestamp) > new Date(lastTimestamp);
  }
}
